import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { deleteEmailJournal, PersonEmailMessage } from "../../api";
import {
	EmailJournalPerson,
	getEmailJournalPersonsByEmailJournalId,
} from "../../api/email-journal-person";

export const EmailDebugDialog = ({
	open,
	onClose,
	email,
	personId,
	onDelete,
}: {
	open: boolean;
	onClose: () => void;
	email: PersonEmailMessage | null;
	personId: number;
	onDelete?: () => void;
}) => {
	const theme = useTheme();
	const [isDeleting, setIsDeleting] = useState(false);
	const [emailJournalPersons, setEmailJournalPersons] = useState<
		EmailJournalPerson[]
	>([]);
	const [loadingEmailJournalPersons, setLoadingEmailJournalPersons] = useState(false);

	useEffect(() => {
		if (open && email?.email_journal_id) {
			setLoadingEmailJournalPersons(true);
			getEmailJournalPersonsByEmailJournalId(email.email_journal_id)
				.then((response) => {
					setEmailJournalPersons(response.results || []);
				})
				.catch((error) => {
					console.error("Error fetching EmailJournalPerson records:", error);
					setEmailJournalPersons([]);
				})
				.finally(() => {
					setLoadingEmailJournalPersons(false);
				});
		} else {
			setEmailJournalPersons([]);
		}
	}, [open, email?.email_journal_id]);

	const handleDelete = async () => {
		if (!email || !window.confirm("Are you sure you want to delete this email?")) {
			return;
		}

		setIsDeleting(true);
		try {
			// Delete the EmailJournal parent (which will CASCADE delete EmailJournalText child)
			await deleteEmailJournal(email.email_journal_id);

			alert("Email deleted successfully");
			onDelete?.();
			onClose();
		} catch (error) {
			console.error("Error deleting email:", error);
			alert(
				`Failed to delete email: ${error instanceof Error ? error.message : "Unknown error"}`
			);
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			fullWidth
			slotProps={{ paper: { sx: { width: "95vw" } } }}
		>
			<DialogTitle>Debug: Visa text/text_display</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ mt: 2 }}>
					{/* Basic Info */}
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Basic Info
						</Typography>
						<Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">
									EmailJournalText ID
								</Typography>
								<div>{email?.id}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">
									EmailJournal ID
								</Typography>
								<div>{email?.email_journal_id}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">EmailQueue ID</Typography>
								<div>{email?.emailqueue_id || <em>null</em>}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">From</Typography>
								<div>{email?.email_from}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">To</Typography>
								<div>{email?.email_to}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">Date</Typography>
								<div>{email?.date}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">Created</Typography>
								<div>{email?.created}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">Subject</Typography>
								<div>{email?.subject}</div>
							</Box>
						</Stack>
					</Box>

					{/* Threading Info */}
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Threading Info
						</Typography>
						<Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">message_id</Typography>
								<div style={{ wordBreak: "break-all", fontSize: 13 }}>
									{email?.message_id || <em>null</em>}
								</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">in_reply_to</Typography>
								<div style={{ wordBreak: "break-all", fontSize: 13 }}>
									{email?.in_reply_to || <em>null</em>}
								</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">thread_id</Typography>
								<div style={{ wordBreak: "break-all", fontSize: 13 }}>
									{email?.thread_id || <em>null</em>}
								</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">external_id</Typography>
								<div style={{ wordBreak: "break-all", fontSize: 13 }}>
									{email?.external_id || <em>null</em>}
								</div>
							</Box>
						</Stack>
					</Box>

					{/* Classification */}
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Classification
						</Typography>
						<Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">folder_type</Typography>
								<div>
									{email?.folder_type_display} ({email?.folder_type})
								</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">email_method</Typography>
								<div>
									{email?.email_method_display || <em>null</em>} (
									{email?.email_method ?? "null"})
								</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">folder</Typography>
								<div>{email?.folder}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">username</Typography>
								<div>{email?.username}</div>
							</Box>
						</Stack>
					</Box>

					{/* Bounce Detection */}
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Bounce Detection
						</Typography>
						<Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">
									bounce (is_bounce)
								</Typography>
								<div
									style={{
										color: email?.bounce
											? theme.actionButtons.thumbDown.main
											: "inherit",
										fontWeight: email?.bounce ? "bold" : "normal",
									}}
								>
									{email?.bounce ? "TRUE" : "FALSE"}
								</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">has_bounced</Typography>
								<div
									style={{
										color: email?.has_bounced
											? theme.actionButtons.thumbDown.main
											: "inherit",
										fontWeight: email?.has_bounced
											? "bold"
											: "normal",
									}}
								>
									{email?.has_bounced ? "TRUE" : "FALSE"}
								</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">
									auto_submitted
								</Typography>
								<div
									style={{
										color: email?.auto_submitted
											? theme.palette.warning.light
											: "inherit",
										fontWeight: email?.auto_submitted
											? "bold"
											: "normal",
									}}
								>
									{email?.auto_submitted ? "TRUE" : "FALSE"}
								</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">
									x_failed_recipients
								</Typography>
								<div
									style={{
										color: email?.x_failed_recipients
											? theme.palette.warning.light
											: "inherit",
										fontWeight: email?.x_failed_recipients
											? "bold"
											: "normal",
									}}
								>
									{email?.x_failed_recipients ? "TRUE" : "FALSE"}
								</div>
							</Box>
						</Stack>
					</Box>

					{/* Tracking */}
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Tracking
						</Typography>
						<Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">opened</Typography>
								<div>{email?.opened ? "TRUE" : "FALSE"}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">has_tracking</Typography>
								<div>{email?.has_tracking ? "TRUE" : "FALSE"}</div>
							</Box>
							<Box sx={{ minWidth: 260 }}>
								<Typography variant="caption">computed_url</Typography>
								<div style={{ wordBreak: "break-all" }}>
									{email?.computed_url ? (
										<a
											href={email.computed_url}
											target="_blank"
											rel="noopener noreferrer"
										>
											{email.computed_url}
										</a>
									) : (
										<em>null</em>
									)}
								</div>
							</Box>
						</Stack>
					</Box>

					{/* EmailJournalPerson Records */}
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							EmailJournalPerson Records ({emailJournalPersons.length})
						</Typography>
						{loadingEmailJournalPersons ? (
							<Typography variant="body2">Loading...</Typography>
						) : emailJournalPersons.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No EmailJournalPerson records found
							</Typography>
						) : (
							<Box
								sx={{
									maxHeight: 300,
									overflow: "auto",
									border: 1,
									borderColor: "divider",
								}}
							>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell>ID</TableCell>
											<TableCell>Person ID</TableCell>
											<TableCell>Person Name</TableCell>
											<TableCell>Email Type</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{emailJournalPersons.map((ejp) => (
											<TableRow key={ejp.id}>
												<TableCell>{ejp.id}</TableCell>
												<TableCell>{ejp.person_id}</TableCell>
												<TableCell>{ejp.person_name}</TableCell>
												<TableCell>
													{ejp.email_type_display}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						)}
					</Box>

					{/* Text Content */}
					<Stack direction="row" spacing={2}>
						<Box sx={{ flex: 1 }}>
							<Typography variant="subtitle2">text</Typography>
							<Box
								sx={{
									p: 1,
									border: 1,
									borderColor: "divider",
									minHeight: 100,
									whiteSpace: "pre-wrap",
									fontFamily: "monospace",
									fontSize: 13,
								}}
							>
								{email?.text}
							</Box>
						</Box>
						<Box sx={{ flex: 1 }}>
							<Typography variant="subtitle2">text_display</Typography>
							<Box
								sx={{
									p: 1,
									border: 1,
									borderColor: "divider",
									minHeight: 100,
									whiteSpace: "pre-wrap",
									fontFamily: "monospace",
									fontSize: 13,
								}}
							>
								{email?.text_display}
							</Box>
						</Box>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isDeleting}>
					Close
				</Button>
				<Button
					onClick={handleDelete}
					color="error"
					variant="contained"
					disabled={isDeleting}
					data-component="delete-email-button"
				>
					{isDeleting ? "Deleting..." : "Delete Email"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
